import React, { type ReactNode, useEffect, useRef, useState } from 'react';
import { getAnchorElementFromEvent } from '@noths/polaris-client-utils';

import * as styles from 'src/components/organisms/HeaderCopyBlock/HeaderCopyBlock.styles';

const toggleButtonId = 'headerCopyBlockToggleButton';
const copyBlockId = 'headerCopyBlockContent';

export interface HeaderCopyBlockProps {
  description?: string;
  heading: string;
  onAccordionToggle?: ({ open }: { open: boolean }) => void;
  onDescriptionLinkClick?: (evt: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Sits at the end of the heading's line. */
  productCount?: ReactNode;
  subheading?: string;
}

export const HeaderCopyBlock = ({
  description,
  heading,
  onAccordionToggle,
  onDescriptionLinkClick,
  productCount,
  subheading,
}: HeaderCopyBlockProps) => {
  const renderDescription = description && description !== '<p></p>';
  const hasContent = subheading || renderDescription;
  const [isOpen, setIsOpen] = useState<boolean | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const computedStyle = window.getComputedStyle(contentRef.current);
      const isContentVisible = computedStyle.display !== 'none';
      setIsOpen(isContentVisible);
    }
  }, []);

  const toggleAccordion = () => {
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);
    onAccordionToggle?.({ open: newOpenState });
  };

  const onCopyClick = (event: React.MouseEvent) => {
    if (onDescriptionLinkClick && getAnchorElementFromEvent(event)) {
      onDescriptionLinkClick(event as React.MouseEvent<HTMLAnchorElement>);
    }
  };

  return (
    <div css={styles.headerCopyBlock}>
      <div css={styles.headingRow}>
        <button
          aria-controls={copyBlockId}
          aria-expanded={Boolean(isOpen)}
          css={styles.toggleButton}
          id={toggleButtonId}
          onClick={toggleAccordion}
        >
          <h1 css={styles.heading}>{heading}</h1>
          {hasContent && <span css={styles.icon} data-open={isOpen} data-testid="chevron-icon" />}
        </button>
        {productCount}
      </div>
      {hasContent && (
        <div
          aria-labelledby={toggleButtonId}
          css={styles.accordionContent}
          data-open={isOpen}
          id={copyBlockId}
          ref={contentRef}
          role="region"
        >
          {subheading && <h2 css={styles.subheading}>{subheading}</h2>}
          {renderDescription && (
            <div css={styles.copyBlockContent}>
              <div
                dangerouslySetInnerHTML={{ __html: description }}
                onClick={onCopyClick}
                role="presentation"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
